import { liveReload } from '@shared/services/realtime.service';
import { PageLoaderComponent, PageLoad } from '@app/shared/components/page-loader/page-loader.component';
import { FilterChipsComponent, FilterChip } from '@app/shared/components/filter-bar/filter-chips.component';
import { SearchPillComponent } from '@app/shared/components/filter-bar/search-pill.component';
import { PageTitleComponent } from '@app/shared/components/page-title/page-title.component';
import { inject, Component, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { ModulesService } from '@shared/services/modules.service';
import { CustomMessageService } from '@app/shared/components/custom-message/custom-message.service';
import { LoggerService } from '@app/logger/logger.service';
import { ContractsService } from '@app/shared/services/entities/contracts.service';
import { MatDialog } from '@angular/material/dialog';
import {MatPaginator} from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { ContractsDialogComponent } from './contracts-dialog/contracts-dialog.component';
import { ContractEquipmentComponent } from './contract-equipment/contract-equipment.component';
import { ContractOperationRoomComponent } from './contract-operationRoom/contract-operationRoom.component';
import { ContractPlaceComponent } from './contract-place/contract-place.component';
import { ContractPriceComponent } from './contract-price/contract-price.component';
import { ContractProductComponent } from './contract-product/contract-product.component';
import { ContractPackagesComponent } from './contract-packages/contract-packages.component';
import { ContractProcedureComponent } from './contract-procedure/contract-procedure.component';
import { ContractSpecialtyComponent } from './contract-specialty/contract-specialty.component';
import { UsersService } from '@app/shared/services/entities/users.service';
import { RolePermissionsService } from '@shared/services/auth/role-permissions.service';
import { NetworkService } from '@app/shared/services/network.service';
import { FormControl } from '@angular/forms';
import { MatSelectChange } from '@angular/material/select';

import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MaterialModule } from '@app/material.module';
const log = new LoggerService('CONTRACT COMPONENT');

@Component({
  standalone: true,
  imports: [PageLoaderComponent, SearchPillComponent, FilterChipsComponent, PageTitleComponent, 
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MaterialModule,
  ],
  selector: 'app-contracts',
  templateUrl: './contracts.component.html',
  styleUrls: ['./contracts.component.scss'],
  encapsulation: ViewEncapsulation.Emulated,
})
export class ContractsComponent implements OnInit {
  private modulesSvcTitle = inject(ModulesService);
  /** Título del módulo = etiqueta visible del catálogo de Módulos. */
  title = this.modulesSvcTitle.moduleTitle('/contracts', 'Contratos');

  isSlowConnection = false; //Validar conexión de red

  public tableName = 'contracts';
  public moduleName1 = 'Contratos';
  public moduleName2 = 'contrato';
  public dataSource = new MatTableDataSource([]);
  public inputs = [
    {
      'name': 'name',
      'displayName': 'Nombre',
      'type': 'text',
      'required': true
    },
    {
      'name': 'description',
      'displayName': 'Descripción',
      'type': 'text',
      'required': true
    },
    {
      'name': 'budget',
      'displayName': 'Presupuesto',
      'type': 'number',
      'required': true
    },
    {
      'name': 'startsOn',
      'displayName': 'Fecha de inicio',
      'type': 'date',
      'required': true
    },
    {
      'name': 'endsOn',
      'displayName': 'Fecha de finalización',
      'type': 'date',
      'required': true
    }
  ];
  public dataList = [];
  public userCustomParams = null;
  public permissions = null;
  statusFormControl = new FormControl();

  @ViewChild(MatPaginator) paginator: MatPaginator;

  constructor(
    private contractsService: ContractsService,
    private customMessageService: CustomMessageService,
    private usersService: UsersService,
    private permissionsService: RolePermissionsService,
    public dialog: MatDialog,
    private networkService: NetworkService,
  ) {}

  /** Pantalla de carga del módulo: bloquea hasta terminar la carga inicial. */
  readonly pageLoad = new PageLoad();
  /** Tiempo real: recarga cuando otro usuario cambia estos módulos (WebSocket). */
  private readonly live = liveReload(['contracts'], () => this.pageLoad.ready && this.pageLoad.refresh(this.getEntityData()));

  ngOnInit(): void {
    //Validar el estado de la conexión
    this.networkService.isConnectionSlow$.subscribe(slow => {
      this.isSlowConnection = slow;
    });
    this.statusFormControl.valueChanges.subscribe(value => {
       this.statusFilter(value);
    });
    void this.init();
  }

  /** Carga inicial (también la usa "Reintentar"). */
  init(): Promise<boolean> {
    return this.pageLoad.run(() => this.initialLoad());
  }

  private async initialLoad(): Promise<void> {
    await this.getRolNameByUserId();
    await this.getEntityData();
  }

  /** Estatus elegido en la barra de filtros. */
  contractStatus: string = 'Todos';
  get statusChips(): FilterChip[] {
    return this.contractStatus && this.contractStatus !== 'Todos' ? [{ key: 'status', label: 'Estatus', value: this.contractStatus }] : [];
  }
  clearStatus(): void {
    this.contractStatus = 'Todos';
    this.statusFilter({ value: 'Todos' } as MatSelectChange);
  }

  /*** @description Filtro de status*/
  statusFilter(event: MatSelectChange){
    let value = event.value
    let data = this.dataList;
    if(value === 'Activo') data = data.filter(d => d.status === true);
    else if(value === 'Inactivo') data = data.filter(d => d.status === false)
    this.dataSource = new MatTableDataSource(data);
    this.dataSource.paginator = this.paginator;
  }

   /*** @description Filtro de texto*/
   applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value
      .replace(/&|<|>|"|'/g, match => ({'&': '&amp;','<': '&lt;','>': '&gt;','"': '&quot;',"'": '&#39;'}[match]!)) //Sanitizar caracteres especiales en HTML
      .replace(/[^a-zA-Z0-9áéíóú-\s]/g, '')     // Solo permitir caracteres alfannuméricos y espacios
      .trim();
    this.dataSource.filter = filterValue.trim().toLowerCase();
    this.dataSource.paginator = this.paginator;
  }

  /**
   * @description Busca información de entidad
   */
  async getEntityData() {
    this.customMessageService.open({title: 'Espere', type: 'neutral', message: 'Cargando información...', loading: true });
    try {
      let response: Utils.ApiResponse<any> = await this.contractsService.search({searchType:'default',condition:{deleted:false}});
      //log.debug(response)
      if (response.data[0] === null) {
        this.customMessageService.close();
        await this.customMessageService.open({title: 'Información', type: 'neutral', message: 'No se encontraron registros', duration: 2000 }); 
        return;
      }
      this.dataList = response['data'];
      //log.debug(this.dataList)
      this.dataSource = new MatTableDataSource(this.dataList);
      this.dataSource.paginator = this.paginator;
      this.customMessageService.close();
    }
    catch(err){
      this.customMessageService.close();
      await this.customMessageService.open({title: 'Error', type: 'error', message: 'Error al cargar datos', dismissBtn: true, dismissBtnText: 'Cerrar' });
    }
    
  }

  /**
   * @description Ventana de dialogo para creación y edición 
   */
  openDialog(type: String, record?: any, index?: number){
    const dialogRef = this.dialog.open(ContractsDialogComponent, {
      data: {
        module: this.moduleName2,
        type: type, 
        record: record,
        inputs: this.inputs
        
      },
    });

    dialogRef.afterClosed().subscribe(async result => {
      if(result && type == 'create'){
        let row = result;
        this.dataList = [row, ...this.dataList];
        this.dataSource = new MatTableDataSource(this.dataList);
      }else if(result && type == 'update'){
        let rowToUpdate = this.dataList.find(x => x['id'] == result.id);
        for (const key of this.inputs) {
          rowToUpdate[key['name']] = result[key['name']];
        }
        this.dataSource = new MatTableDataSource(this.dataList);
        this.dataSource.paginator = this.paginator;
      }

    });
  }

          /**
   * @description Abrir diálogo de procedimientos del contrato
   */
  openContractEquipment(contract: any) {
    try {
      const dialogRef = this.dialog.open(ContractEquipmentComponent, {
        disableClose: true,
        width: '90%',
        maxWidth: '1200px',
        data: {
          contract:contract,
          userCustomParams: this.userCustomParams,
          permissions: this.permissions
        }
      });

      dialogRef.afterClosed().subscribe(async result => {
        if (result) {
          // Los procedimientos fueron guardados exitosamente
          log.debug('Equipos actualizados para el contrato:', contract.name);
          
          // Opcional: Recargar el contrato específico para actualizar la vista
          await this.refreshContract(contract.id);
        } else {
          // El usuario canceló o cerró sin guardar
          log.debug('Gestión de procedimientos cancelada');
        }
      });
    } catch (err) {
      this.customMessageService.open({
        title: 'Error',
        type: 'error',
        message: `Ocurrió un error al abrir la gestión de procedimientos. ${err}`,
        dismissBtn: true,
        dismissBtnText: 'Cerrar'
      });
    }
  }

        /**
   * @description Abrir diálogo de procedimientos del contrato
   */
  openContractOperationRoom(contract: any) {
    try {
      const dialogRef = this.dialog.open(ContractOperationRoomComponent, {
        disableClose: true,
        width: '90%',
        maxWidth: '1200px',
        data: {
          contract:contract,
          userCustomParams: this.userCustomParams,
          permissions: this.permissions
        }
      });

      dialogRef.afterClosed().subscribe(async result => {
        if (result) {
          // Los procedimientos fueron guardados exitosamente
          log.debug('Quirofanos actualizados para el contrato:', contract.name);
          
          // Opcional: Recargar el contrato específico para actualizar la vista
          await this.refreshContract(contract.id);
        } else {
          // El usuario canceló o cerró sin guardar
          log.debug('Gestión de procedimientos cancelada');
        }
      });
    } catch (err) {
      this.customMessageService.open({
        title: 'Error',
        type: 'error',
        message: `Ocurrió un error al abrir la gestión de procedimientos. ${err}`,
        dismissBtn: true,
        dismissBtnText: 'Cerrar'
      });
    }
  }

      /**
   * @description Abrir diálogo de procedimientos del contrato
   */
  openContractPlace(contract: any) {
    try {
      const dialogRef = this.dialog.open(ContractPlaceComponent, {
        disableClose: true,
        width: '90%',
        maxWidth: '1200px',
        data: {
          contract:contract,
          userCustomParams: this.userCustomParams,
          permissions: this.permissions
        }
      });

      dialogRef.afterClosed().subscribe(async result => {
        if (result) {
          // Los procedimientos fueron guardados exitosamente
          log.debug('Especialidades actualizados para el contrato:', contract.name);
          
          // Opcional: Recargar el contrato específico para actualizar la vista
          await this.refreshContract(contract.id);
        } else {
          // El usuario canceló o cerró sin guardar
          log.debug('Gestión de procedimientos cancelada');
        }
      });
    } catch (err) {
      this.customMessageService.open({
        title: 'Error',
        type: 'error',
        message: `Ocurrió un error al abrir la gestión de procedimientos. ${err}`,
        dismissBtn: true,
        dismissBtnText: 'Cerrar'
      });
    }
  }

        /**
   * @description Abrir diálogo de procedimientos del contrato
   */
  openContractPrice(contract: any) {
    try {
      const dialogRef = this.dialog.open(ContractPriceComponent, {
        disableClose: true,
        width: '90%',
        maxWidth: '1200px',
        data: {
          contract:contract,
          userCustomParams: this.userCustomParams,
          permissions: this.permissions
        }
      });

      dialogRef.afterClosed().subscribe(async result => {
        if (result) {
          // Los procedimientos fueron guardados exitosamente
          log.debug('Especialidades actualizados para el contrato:', contract.name);
          
          // Opcional: Recargar el contrato específico para actualizar la vista
          await this.refreshContract(contract.id);
        } else {
          // El usuario canceló o cerró sin guardar
          log.debug('Gestión de procedimientos cancelada');
        }
      });
    } catch (err) {
      this.customMessageService.open({
        title: 'Error',
        type: 'error',
        message: `Ocurrió un error al abrir la gestión de procedimientos. ${err}`,
        dismissBtn: true,
        dismissBtnText: 'Cerrar'
      });
    }
  }

        /**
   * @description Abrir diálogo de procedimientos del contrato
   */
  openContractProduct(contract: any) {
    try {
      const dialogRef = this.dialog.open(ContractProductComponent, {
        disableClose: true,
        width: '90%',
        maxWidth: '1200px',
        data: {
          contract:contract,
          userCustomParams: this.userCustomParams,
          permissions: this.permissions
        }
      });

      dialogRef.afterClosed().subscribe(async result => {
        if (result) {
          // Los procedimientos fueron guardados exitosamente
          log.debug('Especialidades actualizados para el contrato:', contract.name);
          
          // Opcional: Recargar el contrato específico para actualizar la vista
          await this.refreshContract(contract.id);
        } else {
          // El usuario canceló o cerró sin guardar
          log.debug('Gestión de procedimientos cancelada');
        }
      });
    } catch (err) {
      this.customMessageService.open({
        title: 'Error',
        type: 'error',
        message: `Ocurrió un error al abrir la gestión de procedimientos. ${err}`,
        dismissBtn: true,
        dismissBtnText: 'Cerrar'
      });
    }
  }

  /**
   * @description Abrir diálogo de procedimientos del contrato
   */
  /**
   * @description Abrir diálogo de paquetes de servicio del contrato
   */
  openContractPackages(contract: any) {
    try {
      const dialogRef = this.dialog.open(ContractPackagesComponent, {
        disableClose: true,
        width: '90%',
        maxWidth: '1200px',
        data: {
          contract: contract,
          userCustomParams: this.userCustomParams,
          permissions: this.permissions
        }
      });
      dialogRef.afterClosed().subscribe(async result => {
        if (result) await this.refreshContract(contract.id);
      });
    } catch (err) {
      this.customMessageService.open({
        title: 'Error',
        type: 'error',
        message: `Ocurrió un error al abrir los paquetes de servicio. ${err}`,
        dismissBtn: true,
        dismissBtnText: 'Cerrar'
      });
    }
  }

  openContractProcedure(contract: any) {
    try {
      const dialogRef = this.dialog.open(ContractProcedureComponent, {
        disableClose: true,
        width: '90%',
        maxWidth: '1200px',
        data: {
          contract:contract,
          userCustomParams: this.userCustomParams,
          permissions: this.permissions
        }
      });

      dialogRef.afterClosed().subscribe(async result => {
        if (result) {
          // Los procedimientos fueron guardados exitosamente
          log.debug('Procedimientos actualizados para el contrato:', contract.name);
          
          // Opcional: Recargar el contrato específico para actualizar la vista
          await this.refreshContract(contract.id);
        } else {
          // El usuario canceló o cerró sin guardar
          log.debug('Gestión de procedimientos cancelada');
        }
      });
    } catch (err) {
      this.customMessageService.open({
        title: 'Error',
        type: 'error',
        message: `Ocurrió un error al abrir la gestión de procedimientos. ${err}`,
        dismissBtn: true,
        dismissBtnText: 'Cerrar'
      });
    }
  }

    /**
   * @description Abrir diálogo de procedimientos del contrato
   */
  openContractSpecialty(contract: any) {
    try {
      const dialogRef = this.dialog.open(ContractSpecialtyComponent, {
        disableClose: true,
        width: '90%',
        maxWidth: '1200px',
        data: {
          contract:contract,
          userCustomParams: this.userCustomParams,
          permissions: this.permissions
        }
      });

      dialogRef.afterClosed().subscribe(async result => {
        if (result) {
          // Los procedimientos fueron guardados exitosamente
          log.debug('Especialidades actualizados para el contrato:', contract.name);
          
          // Opcional: Recargar el contrato específico para actualizar la vista
          await this.refreshContract(contract.id);
        } else {
          // El usuario canceló o cerró sin guardar
          log.debug('Gestión de procedimientos cancelada');
        }
      });
    } catch (err) {
      this.customMessageService.open({
        title: 'Error',
        type: 'error',
        message: `Ocurrió un error al abrir la gestión de procedimientos. ${err}`,
        dismissBtn: true,
        dismissBtnText: 'Cerrar'
      });
    }
  }

  /**
   * @description Refrescar un contrato específico después de actualizar procedimientos
   */
  async refreshContract(contractId: number) {
    try {
      // Opcional: Puedes recargar solo el contrato específico
      // o simplemente recargar toda la lista
      const response: Utils.ApiResponse<any> = await this.contractsService.search({
        searchType: 'default',
        condition: { id: contractId, deleted: false }
      });

      if (response.data && response.data.length > 0) {
        const updatedContract = response.data[0];
        const index = this.dataList.findIndex(c => c.id === contractId);
        
        if (index !== -1) {
          // Actualizar el contrato en la lista
          this.dataList[index] = updatedContract;
          this.dataSource = new MatTableDataSource(this.dataList);
          this.dataSource.paginator = this.paginator;
        }
      }
    } catch (err) {
      log.error('Error al refrescar contrato:', err);
      // No mostrar error al usuario ya que es una actualización opcional
    }
  }

  public async deleteRecord(record: any, index: number) {
    try{
      const deleteConfirm = await this.customMessageService.open({
        title: 'Cuidado',
        type: 'warning',
        message: `¿Seguro que quieres eliminar el registro <b>${record.name}</b> de forma permanente?`,
        acceptBtn: true,
        dismissBtn: true,
      });

      if (deleteConfirm) {
        this.customMessageService.open({
          title: 'Espere',
          type: 'success',
          message: 'Borrando la información',
          duration: 2000,
          loading: true,
        });
        let data = {
          id : record['id'],
          deleted : true,
          operationType: 'single'
        }
        const apiResponse = await this.contractsService.update(data);
        this.customMessageService.close();
        if (apiResponse.state == 'success') {
          this.dataList.splice(index, 1);
          this.dataSource = new MatTableDataSource(this.dataList);
          this.dataSource.paginator = this.paginator;
          this.customMessageService.open({title: '¡Hecho!', type: 'success',message: apiResponse.message,duration: 2000});
        } else {
          this.customMessageService.open({title: 'Error', type: 'error',message: apiResponse.message,duration: 2000});
          throw new Error(apiResponse.message);
        }
      }
    }catch(err){
      this.customMessageService.close();
      await this.customMessageService.open({title: 'Error', type: 'error', message: err, dismissBtn: true });
    }
  }


  /**
   * @description Cambiar estatus de contrato
   */
  async switchStatus(event: any, item: any, index: number){
    let checks = 0;
    this.dataList.forEach(element => { if(element['status'] == 'Activo') checks += 1 });
    this.customMessageService.open({title: 'Espere', type: 'neutral', message: 'Actualizando estatus de contrato...', loading: true });
    item['status'] =event.checked ? true : false;
    log.debug('item',item)
    let data = {
      id : item['id'],
      status : item['status'],
      operationType: 'single'
    }
    let apiResponse = await this.contractsService.update(data); 
    this.customMessageService.close();
    if(apiResponse.state == 'success'){
      this.customMessageService.open({title: '¡Hecho!', type: 'success',message: apiResponse.message,duration: 2000});
    } else {
      this.customMessageService.open({title: 'Error', type: 'error',message: apiResponse.message,duration: 2000});
      throw new Error(apiResponse.message);
    }
  }


  /**
   * @description Get rol name by user id
   */
  async getRolNameByUserId() {
    const userId = sessionStorage.getItem('userId');
    this.customMessageService.open({
      title: 'Espere',
      type: 'success',
      message: 'Cargando información de usuario',
      loading: true,
    });
    let apiResponse: any = await this.usersService.getRolNameByUserId({id: userId});
    this.customMessageService.close()
    if (apiResponse['data'][0] === null) {
      await this.customMessageService.open({
        title: 'Información',
        type: 'neutral',
        message:
          'Su usuario no cuenta con roles.<br> <b>Favor de solicitar la actualización de su usuario.</b>',
        duration: 2000,
      });
      return;
    }
    this.userCustomParams = apiResponse['data'][0];
    this.permissions = await this.permissionsService.forRoute('/contracts');
    this.customMessageService.close();
  }
}